import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { useIntl, FormattedMessage } from 'react-intl'

import {
  GET_CAMPAIGN,
  GET_CAMPAIGN_LOGS,
  GET_CAMPAIGN_TARGETS,
  GET_MESSAGE_TEMPLATES,
} from '../../gql/queries/campaigns'
import {
  ADD_CAMPAIGN_TARGETS,
  CLEAR_CAMPAIGN_TARGETS,
  DELETE_CAMPAIGN,
  DELETE_CAMPAIGN_TARGET,
  LAUNCH_CAMPAIGN,
  PAUSE_CAMPAIGN,
  REQUEST_CAMPAIGN_LAUNCH,
  SEND_CAMPAIGN_TEST_MESSAGE,
  SYNC_CAMPAIGN_TARGETS,
  UPDATE_CAMPAIGN,
  UPDATE_MESSAGE_TEMPLATE,
} from '../../gql/mutations/campaigns'
import {
  CampaignChannel,
  ICampaign,
  ICampaignLogsRes,
  ICampaignRes,
  ICampaignTargetsRes,
  IMessageTemplate,
  IMessageTemplatesRes,
} from '../../types/campaigns'
import { useGetUser } from '../../store/userStore'
import { CampaignDetail } from './components/CampaignDetail'
import { CampaignFormModal, CampaignFormValues } from './components/CampaignFormModal'
import { TemplateFormModal } from './components/TemplateFormModal'
import { AddTargetModal } from './components/AddTargetModal'
import { CampaignsNav } from './components/CampaignsNav'
import { CAMPAIGN_MESSAGES as M } from './campaignMessages'


export const CampaignDetailPage = () => {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userGroups = useGetUser((s) => s.user?.groups ?? [])
  const isAsesor = userGroups.includes('Asesor')

  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [targetModalOpen, setTargetModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [targetsPage, setTargetsPage] = useState(1)
  const TARGETS_PAGE_SIZE = 50
  const [logsPage, setLogsPage] = useState(1)
  const LOGS_PAGE_SIZE = 50

  const {
    data: campaignData,
    loading: loadingCampaign,
    error: campaignError,
    refetch: refetchCampaign,
  } = useQuery<ICampaignRes>(GET_CAMPAIGN, {
    skip: !id,
    variables: { id },
    fetchPolicy: 'cache-and-network',
    pollInterval: autoRefresh ? 30000 : 0,
  })

  const {
    data: targetsData,
    loading: loadingTargets,
    refetch: refetchTargets,
  } = useQuery<ICampaignTargetsRes>(GET_CAMPAIGN_TARGETS, {
    skip: !id,
    variables: { campaignId: id, page: targetsPage, pageSize: TARGETS_PAGE_SIZE },
    fetchPolicy: 'no-cache',
  })


  const {
    data: logsData,
    loading: loadingLogs,
    refetch: refetchLogs,
  } = useQuery<ICampaignLogsRes>(GET_CAMPAIGN_LOGS, {
    skip: !id,
    variables: { campaignId: id, page: logsPage, pageSize: LOGS_PAGE_SIZE },
    fetchPolicy: 'no-cache',
    pollInterval: autoRefresh ? 30000 : 0,
  })

  const { data: templatesData, refetch: refetchTemplates } = useQuery<IMessageTemplatesRes>(
    GET_MESSAGE_TEMPLATES,
    { fetchPolicy: 'cache-and-network' }
  )

  const campaign: ICampaign | undefined = campaignData?.campaign

  const [updateCampaignMutation, { loading: updatingCampaign }] = useMutation(UPDATE_CAMPAIGN)
  const [stopCampaignMutation, { loading: stoppingCampaign }] = useMutation(UPDATE_CAMPAIGN)
  const [updateTemplateMutation, { loading: updatingTemplate }] = useMutation(UPDATE_MESSAGE_TEMPLATE)
  const [addTargetsMutation, { loading: addingTarget }] = useMutation(ADD_CAMPAIGN_TARGETS)
  const [syncTargetsMutation, { loading: syncingTargets }] = useMutation(SYNC_CAMPAIGN_TARGETS)
  const [launchCampaignMutation, { loading: launchingCampaign }] = useMutation(LAUNCH_CAMPAIGN)
  const [pauseCampaignMutation, { loading: pausingCampaign }] = useMutation(PAUSE_CAMPAIGN)
  const [requestLaunchMutation, { loading: requestingLaunch }] = useMutation(REQUEST_CAMPAIGN_LAUNCH)
  const [sendTestMutation] = useMutation(SEND_CAMPAIGN_TEST_MESSAGE)
  const [deleteCampaignTargetMutation] = useMutation(DELETE_CAMPAIGN_TARGET)
  const [clearCampaignTargetsMutation, { loading: clearingTargets }] = useMutation(CLEAR_CAMPAIGN_TARGETS)
  const [deleteCampaignMutation] = useMutation(DELETE_CAMPAIGN)

  const handleSubmitCampaign = async (values: CampaignFormValues) => {
    if (!campaign) return
    try {
      await updateCampaignMutation({
        variables: {
          id: campaign.id,
          name: values.name,
          description: values.description,
          enabledChannels: values.enabledChannels,
          strategy: values.strategy,
          scheduleType: values.scheduleType,
          scheduledTime: values.scheduledTime,
          recurringRule: values.recurringRule || null,
          targetSegment: values.targetSegment,
          templateId: values.templateId,
          metadata: values.metadata,
        },
      })
      toast.success(intl.formatMessage(M.toastCampaignUpdated))
      setCampaignModalOpen(false)
      await refetchCampaign()
    } catch {
      toast.error(intl.formatMessage(M.toastCampaignSaveError))
    }
  }

  const handleSubmitTemplate = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
  }) => {
    if (!editingTemplate) return
    try {
      await updateTemplateMutation({ variables: { id: editingTemplate.id, ...payload } })
      toast.success(intl.formatMessage(M.toastTemplateUpdated))
      setTemplateModalOpen(false)
      await refetchTemplates()
    } catch {
      toast.error(intl.formatMessage(M.toastTemplateSaveError))
    }
  }

  const handleAddTarget = async (
    targets: { contactId: string; metadata?: Record<string, any>; channelOverrides?: Record<string, any> }[]
  ) => {
    if (!id) return
    try {
      await addTargetsMutation({
        variables: {
          campaignId: id,
          targets: targets.map((t) => ({
            contactId: t.contactId,
            metadata: t.metadata || null,
            channelOverrides: t.channelOverrides || null,
          })),
        },
      })
      toast.success(intl.formatMessage(M.toastTargetsAdded, { count: targets.length }))
      setTargetModalOpen(false)
      await Promise.all([refetchCampaign(), refetchTargets()])
    } catch {
      toast.error(intl.formatMessage(M.toastAddTargetsError))
    }
  }

  const handleSyncTargets = async (selectedContactIds: string[]) => {
    if (!id) return
    try {
      const result = await syncTargetsMutation({
        variables: { campaignId: id, contactIds: selectedContactIds },
      })
      const added = result.data?.syncCampaignTargets?.addedCount ?? 0
      const removed = result.data?.syncCampaignTargets?.removedCount ?? 0
      const parts: string[] = []
      if (added > 0) parts.push(intl.formatMessage(M.toastTargetsAddedPart, { count: added }))
      if (removed > 0) parts.push(intl.formatMessage(M.toastTargetsRemovedPart, { count: removed }))
      toast.success(
        parts.length > 0
          ? intl.formatMessage(M.toastTargetsUpdated, { summary: parts.join(', ') })
          : intl.formatMessage(M.toastNoChanges)
      )
      setTargetModalOpen(false)
      await Promise.all([refetchCampaign(), refetchTargets()])
    } catch {
      toast.error(intl.formatMessage(M.toastSaveTargetsError))
    }
  }

  const handleLaunchCampaign = async () => {
    if (!id) return
    try {
      await launchCampaignMutation({ variables: { id, runAsync: true } })
      toast.success(intl.formatMessage(M.toastQueued))
      await Promise.all([refetchCampaign(), refetchLogs()])
    } catch {
      toast.error(intl.formatMessage(M.toastLaunchError))
    }
  }

  const handlePauseCampaign = async () => {
    if (!id) return
    try {
      await pauseCampaignMutation({ variables: { id } })
      toast.success(intl.formatMessage(M.toastPaused))
      await refetchCampaign()
    } catch {
      toast.error(intl.formatMessage(M.toastPauseError))
    }
  }

  const handleRequestLaunchApproval = async () => {
    if (!id) return
    try {
      const { data } = await requestLaunchMutation({ variables: { campaignId: id } })
      if (data?.requestCampaignLaunch?.ok) {
        const count = data.requestCampaignLaunch.notifiedCount ?? 1
        toast.success(intl.formatMessage(M.toastApprovalSent, { count }))
      } else {
        const errors = data?.requestCampaignLaunch?.errors
        toast.error(errors?.[0] ?? intl.formatMessage(M.toastApprovalError))
      }
    } catch {
      toast.error(intl.formatMessage(M.toastApprovalError))
    }
  }

  const handleStopCampaign = async () => {
    if (!id) return
    try {
      await stopCampaignMutation({ variables: { id, status: 'COMPLETED' } })
      toast.success(intl.formatMessage(M.toastRecurringStopped))
      await refetchCampaign()
    } catch {
      toast.error(intl.formatMessage(M.toastStopError))
    }
  }

  const handleDeleteCampaign = async () => {
    if (!id) return
    try {
      await deleteCampaignMutation({ variables: { id } })
      toast.success(intl.formatMessage(M.toastCampaignDeleted))
      navigate('/campaigns')
    } catch {
      toast.error(intl.formatMessage(M.toastDeleteError))
    }
  }

  const handleRemoveTarget = async (targetId: string) => {
    try {
      await deleteCampaignTargetMutation({ variables: { id: targetId } })
      toast.success(intl.formatMessage(M.toastTargetRemoved))
      await Promise.all([refetchTargets(), refetchCampaign()])
    } catch {
      toast.error(intl.formatMessage(M.toastRemoveTargetError))
    }
  }

  const handleClearTargets = async () => {
    if (!id) return
    try {
      const { data: result } = await clearCampaignTargetsMutation({ variables: { campaignId: id } })
      const deleted = result?.clearCampaignTargets?.deletedCount ?? 0
      toast.success(intl.formatMessage(M.toastTargetsRemoved, { count: deleted }))
      await Promise.all([refetchTargets(), refetchCampaign()])
    } catch {
      toast.error(intl.formatMessage(M.toastClearTargetsError))
    }
  }

  const handleSendTest = async (
    recipient: string,
    channels: CampaignChannel[],
    advancedOptions?: { fromEmail?: string; fromName?: string; replyTo?: string; includeLogo?: boolean }
  ) => {
    if (!campaign?.template?.id) {
      toast.error(intl.formatMessage(M.toastSelectTemplate))
      return
    }
    try {
      await sendTestMutation({
        variables: { templateId: campaign.template.id, recipient, channels, ...advancedOptions },
      })
      toast.success(intl.formatMessage(M.toastTestSent))
    } catch {
      toast.error(intl.formatMessage(M.toastTestError))
      throw new Error('Send test failed')
    }
  }

  const templates = templatesData?.messageTemplates ?? []

  if (campaignError) {
    return (
      <Alert variant='danger'>
        <FormattedMessage {...M.detailLoadError} values={{ message: campaignError.message }} />
      </Alert>
    )
  }

  return (
    <div className='campaign-detail-page'>
      <CampaignsNav />
      <div className='d-flex align-items-center gap-3 mb-6'>
        <button
          className='btn btn-sm btn-light'
          onClick={() => navigate('/campaigns')}
        >
          <i className='bi bi-arrow-left me-1'></i>
          <FormattedMessage {...M.back} />
        </button>
        {loadingCampaign && <Spinner animation='border' size='sm' />}
      </div>

      <CampaignDetail
        campaign={campaign}
        targetsData={targetsData?.campaignTargets?.items}
        logsData={logsData?.campaignLogs?.items}
        loadingTargets={loadingTargets}
        loadingLogs={loadingLogs}
        targetsPage={targetsPage}
        targetsTotalPages={targetsData?.campaignTargets?.totalPages}
        targetsTotalCount={targetsData?.campaignTargets?.totalCount}
        targetsPageSize={TARGETS_PAGE_SIZE}
        onTargetsPageChange={(p) => setTargetsPage(p)}
        logsPage={logsPage}
        logsTotalPages={logsData?.campaignLogs?.totalPages}
        logsTotalCount={logsData?.campaignLogs?.totalCount}
        logsPageSize={LOGS_PAGE_SIZE}
        onLogsPageChange={(p) => setLogsPage(p)}
        onEditCampaign={() => setCampaignModalOpen(true)}
        onAddTarget={() => setTargetModalOpen(true)}
        isAsesor={isAsesor}
        onLaunchCampaign={handleLaunchCampaign}
        onRequestLaunchApproval={handleRequestLaunchApproval}
        onPauseCampaign={handlePauseCampaign}
        launching={launchingCampaign || requestingLaunch}
        pausing={pausingCampaign}
        onSendTest={handleSendTest}
        onEditTemplate={(template) => {
          setEditingTemplate(template)
          setTemplateModalOpen(true)
        }}
        onRefreshTargets={() => refetchTargets()}
        onRefreshLogs={() => refetchLogs()}
        onRemoveTarget={handleRemoveTarget}
        onClearTargets={handleClearTargets}
        clearingTargets={clearingTargets}
        onStopCampaign={handleStopCampaign}
        stopping={stoppingCampaign}
        onDelete={handleDeleteCampaign}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={setAutoRefresh}
      />

      <CampaignFormModal
        show={campaignModalOpen}
        onHide={() => setCampaignModalOpen(false)}
        onSubmit={handleSubmitCampaign}
        templates={templates}
        loading={updatingCampaign}
        campaign={campaign ?? null}
      />
      <TemplateFormModal
        show={templateModalOpen}
        onHide={() => setTemplateModalOpen(false)}
        onSubmit={handleSubmitTemplate}
        template={editingTemplate}
        loading={updatingTemplate}
      />
      <AddTargetModal
        show={targetModalOpen}
        onHide={() => setTargetModalOpen(false)}
        onSubmit={handleAddTarget}
        onSync={handleSyncTargets}
        loading={addingTarget || syncingTargets}
        campaignName={campaign?.name}
        campaignId={id}
        initialTargetCount={campaign?.totalTargets ?? 0}
        onBulkAdded={() => {
          refetchCampaign()
          refetchTargets()
        }}
      />
    </div>
  )
}
